import React, { useMemo } from 'react'
import {
  Table,
  TableHeaderItem,
  Flex,
  Pill,
  Tooltip,
  useBreakpoint,
} from '@sketch/components'
import {
  DocumentPreviewsLayoutWrapper,
  TableCell,
  TableRow,
  DocumentThumbnail,
  DocumentName,
  LockIcon,
  ArrowIcon,
  SlidersIcon,
  LinkIcon,
  PreviewOverview,
  Actions,
  ActionButton,
  DocumentDetails,
} from './DocumentPreviewsLayout.styles'
import { routes } from '@sketch/modules-common'
import { sortByExpiryDateAndName } from '../../../shares/utils/sortPreviews'

export interface PreviewRowDocument {
  identifier: string
  name: string
  thumbnailUrl?: string

  previews: {
    identifier: string
    name: string
    passwordProtected: boolean
    expiryDate: string | null
  }[]
}

interface PreviewRowProps {
  document: PreviewRowDocument
  onCopyLink: (previewIdentifier: string) => void
  onPreviewSettings: (
    documentIdentifier: string,
    previewIdentifier: string
  ) => void
  onOpenPreview: (previewIdentifier: string) => void
}

const isExpired = (date: string | null) => {
  if (!date) {
    return false
  }

  const parsedDate = new Date(date)
  const now = new Date()

  return parsedDate < now
}

export const PreviewRow = ({
  document,
  onCopyLink,
  onOpenPreview,
  onPreviewSettings,
}: PreviewRowProps) => {
  const isMobile = !useBreakpoint('xs')

  const sortedPreviews = useMemo(
    () =>
      [...document.previews].sort((a, b) => {
        const aExpired = isExpired(a.expiryDate)
        const bExpired = isExpired(b.expiryDate)
        if (aExpired !== bExpired) return aExpired ? 1 : -1
        return sortByExpiryDateAndName(a, b)
      }),
    [document.previews]
  )

  return (
    <TableRow key={`share-${document.identifier}`}>
      {!isMobile && (
        <TableCell>
          <DocumentDetails
            to={routes.SHARE_VIEW.create({ shareID: document.identifier })}
            target="_blank"
          >
            <DocumentThumbnail
              src={document.thumbnailUrl}
              alt={document.name}
            />
            <DocumentName>{document.name}</DocumentName>
          </DocumentDetails>
        </TableCell>
      )}
      <TableCell>
        {sortedPreviews.map(preview => (
          <Flex
            width={350}
            direction="column"
            justifyContent="space-between"
            alignItems="center"
            key={`preview-${preview.identifier}`}
          >
            <PreviewOverview>
              {preview.passwordProtected ? (
                <Tooltip content="Pasword Protected" placement="top">
                  <LockIcon />
                </Tooltip>
              ) : null}
              {preview.name}
              {isExpired(preview.expiryDate) ? (
                <Pill variant="secondary">Expired</Pill>
              ) : null}
            </PreviewOverview>
            <Actions>
              <Tooltip content="Copy Link" placement="top">
                <ActionButton onClick={() => onCopyLink(preview.identifier)}>
                  <LinkIcon />
                </ActionButton>
              </Tooltip>
              <Tooltip content="Edit Preview" placement="top">
                <ActionButton
                  onClick={() =>
                    onPreviewSettings(document.identifier, preview.identifier)
                  }
                >
                  <SlidersIcon />
                </ActionButton>
              </Tooltip>
              <Tooltip content="Open Preview" placement="top">
                <ActionButton onClick={() => onOpenPreview(preview.identifier)}>
                  <ArrowIcon />
                </ActionButton>
              </Tooltip>
            </Actions>
          </Flex>
        ))}
      </TableCell>
    </TableRow>
  )
}

export interface DocumentPreviewsLayoutProps {
  items: PreviewRowDocument[]
  header: TableHeaderItem[]
  renderItem: (item: PreviewRowDocument) => React.ReactNode
}

export const DocumentPreviewsLayout: React.FCC<DocumentPreviewsLayoutProps> = ({
  items,
  header,
  renderItem,
}) => {
  return (
    <DocumentPreviewsLayoutWrapper>
      <Table
        header={header}
        items={items}
        renderItemKey={item => item.identifier}
        renderItem={renderItem}
      />
    </DocumentPreviewsLayoutWrapper>
  )
}
